import './styles/ProfileMenu.scss';

import { NotePencil, SignOut, UserCircle } from '@phosphor-icons/react';
import { LOGIN_MSG, SIGNOUT_ERROR } from '@whistler-ui/k2/constants';
import { useAppMessages, useClassHelper } from '@whistler-ui/k2/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useStoredPreviousUrl } from '../../hooks';
import { routes } from '../../routes';
import { signOut } from '../../services';
import { useAuthStore } from '../../store';
import { useThemeStore } from '../../store/theme';

const componentName = 'c-ProfileMenu';

export const ProfileMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const c = useClassHelper(componentName);
  const { config, colors } = useThemeStore();
  const { clearPreviousUrl } = useStoredPreviousUrl();
  const [setMessage] = useAppMessages(t);

  const signoutWithMsg = async () => {
    try {
      await signOut({ appMsg: LOGIN_MSG.SIGN_OUT_SUCCESS });
      clearPreviousUrl();
    } catch (error) {
      const msg = (error as Error | TypeError).message;
      console.log({ error: msg });

      // Forcing SignOut if any error is throw by k2/helpers/signOutHelper
      if (msg === SIGNOUT_ERROR) {
        await signOut({
          forceSignOut: true,
        });

        window.location.replace(routes.login.url);
      }

      setMessage({
        type: useAppMessages.type.ERROR,
        msg: msg,
      });
    }
  };

  const profileMenuRoutes = [
    {
      icon: <NotePencil size={24} color={colors.colorFontPrimary} />,
      routes: routes.editAccount,
    },
    // {
    //   icon: <GearSix size={24} color={colors.colorFontPrimary} />,
    //   routes: routes.settings
    // },
    {
      icon: <SignOut size={24} color={colors.colorFontPrimary} />,
      functionLabel: signoutWithMsg,
      label: 'componentProfileMenu_sign_out',
    },
  ];

  const UserInfo = () => {
    const {
      authValues: { loggedUser },
    } = useAuthStore();

    return (
      <div className={c('__menu-item', '__no-hover')}>
        <div className={c('__icon')}>
          <UserCircle size={32} color={colors.colorFontPrimary} weight="fill" />
        </div>
        <div className={c('__name_wrapper')}>
          <div className={c('__min-label')}>{`${loggedUser?.first_name} `}</div>
          <div className={c('__sub-label')}>{`${loggedUser?.roles[0]}`}</div>
        </div>
      </div>
    );
  };

  return (
    <div className={componentName}>
      <UserInfo />
      {profileMenuRoutes.map((menuItem, index) => {
        return (
          <a
            href={menuItem?.routes?.url ?? '#'}
            key={`menuItems_${index}`}
            className={c('__menu-item')}
            onClick={evt => {
              evt.preventDefault();

              if (menuItem.functionLabel) {
                return menuItem.functionLabel();
              }

              navigate(menuItem.routes.url);
            }}
          >
            <div className={c('__icon')}>{menuItem.icon}</div>
            <div className={c('__label')}>
              {t(menuItem.label ? menuItem.label : menuItem.routes.displayName)}
            </div>
          </a>
        );
      })}
      <div className={c('__menu-item', '__no-hover', '__top-border')}>
        <div className={c('__terms_conditions')}>
          <a
            target="_blank"
            href={config.environments.settings.privacyPolicyURL}
            rel="noreferrer"
          >
            {t('componentProfileMenu_privacy_policy')}
          </a>
          <a
            target="_blank"
            href={config.environments.settings.termOfServiceUrl}
            rel="noreferrer"
          >
            {t('componentProfileMenu_terms_of_service')}
          </a>
          {config.environments.settings.imprintUrl && (
            <a
              target="_blank"
              href={config.environments.settings.imprintUrl}
              rel="noreferrer"
            >
              {t('componentProfileMenu_imprint')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
